import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ScrollText, Check, AlertCircle } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { tenantsService } from '@/services/platform/tenants';
import { authService } from '@/services/auth';
import { session } from '@/lib/session';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  DOC_LABELS,
  currentVersionFor,
  useCurrentVersions,
  type ConsentDoc,
} from '@/lib/complianceConfig';
import { homeForUser } from '@/config/navigation';
import type { TenantConsents } from '@/types/platform';

/**
 * Forces TENANT_ADMIN to re-accept Terms / Privacy / DPA when the user's
 * tenant.consents are stale vs the platform's current versions. Driven by
 * `me.consentRefreshRequired` (server-computed on /auth/me).
 */
export function ReconsentPage() {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const versionsQuery = useCurrentVersions();

  const stale: ConsentDoc[] = me?.consentRefreshRequired ?? [];
  const [accepted, setAccepted] = useState<Record<ConsentDoc, boolean>>({
    terms: false,
    privacy: false,
    dpa: false,
  });

  // If somehow nothing is stale, hop home immediately.
  if (me && stale.length === 0) {
    navigate(homeForUser(me), { replace: true });
  }

  const allAccepted = stale.every((s) => accepted[s]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!me) throw new Error('No session');
      const versions = versionsQuery.data;
      if (!versions) throw new Error('Versions not loaded');
      const now = new Date().toISOString();

      // Fetch the existing consents block first so we PATCH minimally
      // (preserving fields we don't touch — e.g. dpaSigner).
      const tenant = await tenantsService.get(me.tenantId);
      const next: TenantConsents = {
        ...(tenant.consents ?? {}),
        ...(stale.includes('terms')
          ? { termsVersion: versions.termsVersion, termsAcceptedAt: now }
          : {}),
        ...(stale.includes('privacy')
          ? { privacyVersion: versions.privacyVersion, privacyAcceptedAt: now }
          : {}),
        ...(stale.includes('dpa')
          ? { dpaVersion: versions.dpaVersion, dpaAcceptedAt: now }
          : {}),
      };
      return tenantsService.update(me.tenantId, { consents: next } as never);
    },
    onSuccess: async () => {
      toast.success('Acceptance recorded');
      // Re-hydrate /me so consentRefreshRequired clears, then bounce home.
      try {
        const fresh = await authService.me();
        session.updateUser(fresh);
        qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      } catch {
        /* swallow — gate will recover next request */
      }
      qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      navigate(homeForUser(me), { replace: true });
    },
  });

  if (!me) return null;

  if (versionsQuery.isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Checking compliance posture…" />
      </div>
    );
  }

  if (versionsQuery.error) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Compliance" title="Updated terms — review required" />
        <ErrorState error={versionsQuery.error} onRetry={() => versionsQuery.refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Compliance"
        title="Updated terms — review required"
        description={`ZAFROI has updated documents governing your use of the platform. As ${me.tenantName}'s admin, please review and accept the new versions on the tenant's behalf before continuing.`}
      />

      <div className="rounded-md border border-info/20 bg-info/5 px-4 py-3">
        <div className="flex items-start gap-2.5 text-[12.5px] text-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-info" />
          <div>
            Your acceptance is recorded against the audit trail with your name,
            timestamp, and document version. You will not be prompted again unless
            ZAFROI bumps a version.
          </div>
        </div>
      </div>

      <SectionCard
        title={
          <span className="flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-muted-foreground" />
            Documents to accept
          </span>
        }
        description={`${stale.length} document${stale.length === 1 ? '' : 's'} need re-acceptance`}
      >
        <ul className="space-y-3">
          {stale.map((doc) => {
            const current = currentVersionFor(doc, versionsQuery.data);
            return (
              <li key={doc} className="rounded-md border bg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <h3 className="text-[14px] font-semibold text-foreground">
                        {DOC_LABELS[doc]}
                      </h3>
                      {current && (
                        <Badge variant="muted" className="font-mono text-[10px]">
                          → {current}
                        </Badge>
                      )}
                    </div>
                    <p className="text-[12.5px] text-muted-foreground">
                      {documentSummary(doc)}{' '}
                      <a
                        href={`#${doc}`}
                        className="font-medium text-foreground underline-offset-2 hover:underline"
                      >
                        Read the full document
                      </a>
                    </p>
                  </div>
                  <label className="flex shrink-0 cursor-pointer items-center gap-2 text-[12.5px]">
                    <input
                      type="checkbox"
                      checked={accepted[doc]}
                      onChange={(e) => setAccepted((a) => ({ ...a, [doc]: e.target.checked }))}
                      className="h-4 w-4 rounded border-input accent-primary"
                    />
                    <span className="font-medium text-foreground">I accept</span>
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      </SectionCard>

      {mutation.error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2.5 text-[12.5px] text-destructive"
        >
          {mutation.error instanceof Error ? mutation.error.message : 'Could not record acceptance'}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!allAccepted || mutation.isPending || !versionsQuery.data}
        >
          {mutation.isPending ? 'Recording…' : 'Confirm acceptance'}
          {!mutation.isPending && <Check className="h-3.5 w-3.5" />}
        </Button>
        <p className="text-[11.5px] text-muted-foreground">
          Signed as <strong>{me.fullName}</strong> ({me.email})
        </p>
      </div>
    </div>
  );
}

function documentSummary(doc: ConsentDoc): string {
  if (doc === 'terms') {
    return 'Service terms covering acceptable use, uptime, support and termination.';
  }
  if (doc === 'privacy') {
    return 'How Zafroi handles personal data, lawful bases, retention, and cookies.';
  }
  return 'Data Processing Agreement covering Zafroi as processor of your customer data.';
}
